// src/pages/financial/FinancialDashboard.tsx
import React, { useState, useEffect, lazy, Suspense } from "react";
import { FaSync, FaSpinner, FaMoneyBillWave } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";
import {
  fetchFinancialOverview,
  fetchRevenueDistribution,
} from "./finance/financialApi";

// ✅ Lazy load heavy components
const OverviewTab = lazy(() =>
  import("./finance/OverviewTab").then((m) => ({ default: m.OverviewTab }))
);
const DistributionTab = lazy(() =>
  import("./finance/DistributionTab").then((m) => ({
    default: m.DistributionTab,
  }))
);
const ExpenseTab = lazy(() =>
  import("./finance/ExpenseTab").then((m) => ({ default: m.ExpenseTab }))
);
const HistoryTab = lazy(() =>
  import("./finance/HistoryTab").then((m) => ({ default: m.HistoryTab }))
);

type Tab = "overview" | "distribution" | "expenses" | "history";

const FinancialDashboard = () => {
  const [selectedTab, setSelectedTab] = useState<Tab>("overview");
  const [loading, setLoading] = useState(false);

  const [financialData, setFinancialData] = useState({
    totalRevenue: 0,
    appointmentRevenue: 0,
    courseRevenue: 0,
    totalStudents: 0,
    activeEnrollments: 0,
    currentBalance: 0,
    bankPayments: 0,
    pendingReviews: 0,
    overduePayments: 0,
  });

  const [revenueDistribution, setRevenueDistribution] = useState({
    totalRevenue: 0,
    akashBaseShare: 0,
    g2gBaseShare: 0,
    zaforBaseShare: 0,
    jewelBaseShare: 0,
    totalCosts: 0,
    totalRefunds: 0,
    akashFinalShare: 0,
    g2gFinalShare: 0,
    zaforFinalShare: 0,
    jewelFinalShare: 0,
    akashWithdrawals: 0,
    zaforWithdrawals: 0,
    jewelWithdrawals: 0,
    g2gDeductions: 0,
  });

  // ✅ Only load essential data on mount
  useEffect(() => {
    loadEssentialData();
  }, []);

  const loadEssentialData = async () => {
    setLoading(true);
    try {
      const [overview, distribution] = await Promise.all([
        fetchFinancialOverview(),
        fetchRevenueDistribution(),
      ]);
      setFinancialData(overview);
      setRevenueDistribution(distribution);
    } catch (error) {
      console.error("Error loading financial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await loadEssentialData();
  };

  const TabButton: React.FC<{
    tab: Tab;
    label: string;
    isActive: boolean;
    onClick: (tab: Tab) => void;
  }> = ({ tab, label, isActive, onClick }) => (
    <button
      onClick={() => onClick(tab)}
      className={`px-6 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#009fe3]/30
      ${
        isActive
          ? "bg-[#009fe3] text-white shadow-md"
          : "bg-white text-[#009fe3] border border-[#009fe3]/30 hover:bg-[#009fe3]/10"
      }`}
    >
      {label}
    </button>
  );

  // Loading spinner component
  const LoadingFallback = () => (
    <div className="bg-white rounded-lg shadow-md p-12 text-center">
      <FaSpinner className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
      <p className="text-gray-600">Loading...</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaMoneyBillWave />}
          title="Financial Dashboard"
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Dashboard", to: "/admin/dashboard" },
            { label: "Financial Dashboard" },
          ]}
        />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <LoadingFallback />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaMoneyBillWave />}
        title="Financial Dashboard"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Financial Dashboard" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <button
          onClick={refreshData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base backdrop-blur-sm"
          style={{ fontSize: "16px" }}
        >
          <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            <TabButton
              tab="overview"
              label="Overview"
              isActive={selectedTab === "overview"}
              onClick={setSelectedTab}
            />
            <TabButton
              tab="distribution"
              label="Distribution"
              isActive={selectedTab === "distribution"}
              onClick={setSelectedTab}
            />
            <TabButton
              tab="expenses"
              label="Expenses"
              isActive={selectedTab === "expenses"}
              onClick={setSelectedTab}
            />
            <TabButton
              tab="history"
              label="History"
              isActive={selectedTab === "history"}
              onClick={setSelectedTab}
            />
          </div>
        </div>

        {/* Content with Lazy Loading */}
        <Suspense fallback={<LoadingFallback />}>
          {selectedTab === "overview" && (
            <OverviewTab
              financialData={financialData}
              revenueDistribution={revenueDistribution}
            />
          )}
          {selectedTab === "distribution" && (
            <DistributionTab revenueDistribution={revenueDistribution} />
          )}
          {selectedTab === "expenses" && <ExpenseTab />}
          {selectedTab === "history" && (
            <HistoryTab
              onWithdrawalDeleted={async () => {
                const data = await fetchRevenueDistribution();
                setRevenueDistribution(data);
              }}
            />
          )}
        </Suspense>
      </main>
    </div>
  );
};

export default FinancialDashboard;
